import { addDays, addWeeks, addMonths } from "date-fns";
import { CreditCard } from "lucide-react";
import { useMemo } from "react";
import { DateRange } from "react-day-picker";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";
import {
    getDateKeyForGrouping,
    getWeekStartKey,
    getMonthKeyForGrouping,
    getGroupingGranularity,
    formatDateForDisplay,
    formatWeekRange,
} from "./utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
    ChartContainer,
    ChartTooltip,
    ChartTooltipContent,
    ChartLegend,
    ChartLegendContent,
} from "@/components/ui/chart";
import { CreditTransactionAnalytics } from "@/pages/Admin/Stats/StatsPage";
import { ICreditTransaction } from "@/types/creditTransaction";

interface CreditsAnalyticsProps {
    data: {
        analytics: CreditTransactionAnalytics;
        transactions: ICreditTransaction[];
    };
    dateRange: DateRange;
}

// SPEND action codes
const spendActionCodes = [
    "DOWNLOAD_DOCUMENT",
    "AI_GENERATE_SUMMARY",
    "AI_GENERATE_BULLET_POINTS",
    "AI_WRITING_PROMPT",
    "AI_PROMPT_REWRITE",
    "AI_PROMPT_SHORTEN",
    "AI_PROMPT_FORMAL",
    "AI_PROMPT_GRAMMAR",
    "SMART_MATCH",
    "IMPORT_RESUME",
    "INCREASE_DOCUMENT_LIMIT",
    "ATS_SCAN",
];

// PURCHASE action codes
const purchaseActionCodes = [
    "SMALL_CREDIT_PACK",
    "MEDIUM_CREDIT_PACK",
    "LARGE_CREDIT_PACK",
    "XL_CREDIT_PACK",
];

const spendChartConfig = {
    DOWNLOAD_DOCUMENT: {
        label: "Download Document",
        theme: {
            light: "hsl(var(--admin-chart-1))",
            dark: "hsl(var(--admin-chart-1))",
        },
    },
    AI_GENERATE_SUMMARY: {
        label: "AI Generate Summary",
        theme: {
            light: "hsl(var(--admin-chart-2))",
            dark: "hsl(var(--admin-chart-2))",
        },
    },
    AI_GENERATE_BULLET_POINTS: {
        label: "AI Generate Bullet Points",
        theme: {
            light: "hsl(var(--admin-chart-3))",
            dark: "hsl(var(--admin-chart-3))",
        },
    },
    AI_WRITING_PROMPT: {
        label: "AI Writing Prompt",
        theme: {
            light: "hsl(var(--admin-chart-4))",
            dark: "hsl(var(--admin-chart-4))",
        },
    },
    AI_PROMPT_REWRITE: {
        label: "AI Prompt Rewrite",
        theme: {
            light: "hsl(var(--admin-chart-5))",
            dark: "hsl(var(--admin-chart-5))",
        },
    },
    AI_PROMPT_SHORTEN: {
        label: "AI Prompt Shorten",
        theme: {
            light: "hsl(var(--admin-chart-6))",
            dark: "hsl(var(--admin-chart-6))",
        },
    },
    AI_PROMPT_FORMAL: {
        label: "AI Prompt Formal",
        theme: {
            light: "hsl(var(--admin-chart-7))",
            dark: "hsl(var(--admin-chart-7))",
        },
    },
    AI_PROMPT_GRAMMAR: {
        label: "AI Prompt Grammar",
        theme: {
            light: "hsl(var(--admin-chart-8))",
            dark: "hsl(var(--admin-chart-8))",
        },
    },
    SMART_MATCH: {
        label: "Smart Match",
        theme: {
            light: "hsl(var(--admin-chart-9))",
            dark: "hsl(var(--admin-chart-9))",
        },
    },
    IMPORT_RESUME: {
        label: "Import Resume",
        theme: {
            light: "hsl(var(--admin-chart-10))",
            dark: "hsl(var(--admin-chart-10))",
        },
    },
    INCREASE_DOCUMENT_LIMIT: {
        label: "Increase Document Limit",
        theme: {
            light: "hsl(var(--admin-chart-11))",
            dark: "hsl(var(--admin-chart-11))",
        },
    },
    ATS_SCAN: {
        label: "ATS Scan",
        theme: {
            light: "hsl(var(--admin-chart-1))",
            dark: "hsl(var(--admin-chart-1))",
        },
    },
};

const purchaseChartConfig = {
    SMALL_CREDIT_PACK: {
        label: "Small Credit Pack",
        theme: {
            light: "hsl(var(--admin-chart-1))",
            dark: "hsl(var(--admin-chart-1))",
        },
    },
    MEDIUM_CREDIT_PACK: {
        label: "Medium Credit Pack",
        theme: {
            light: "hsl(var(--admin-chart-2))",
            dark: "hsl(var(--admin-chart-2))",
        },
    },
    LARGE_CREDIT_PACK: {
        label: "Large Credit Pack",
        theme: {
            light: "hsl(var(--admin-chart-3))",
            dark: "hsl(var(--admin-chart-3))",
        },
    },
    XL_CREDIT_PACK: {
        label: "XL Credit Pack",
        theme: {
            light: "hsl(var(--admin-chart-4))",
            dark: "hsl(var(--admin-chart-4))",
        },
    },
};

const generateChartData = (
    transactions: ICreditTransaction[],
    actionCodes: string[],
    granularity: "day" | "week" | "month",
    from: Date,
    to: Date,
) => {
    if (!transactions) return [];

    // Filter transactions by action codes
    const filteredTransactions = transactions.filter(transaction =>
        actionCodes.includes(transaction.actionCode),
    );

    // Group transactions by the chosen granularity
    const groupKeyFn =
        granularity === "month"
            ? getMonthKeyForGrouping
            : granularity === "week"
              ? getWeekStartKey
              : getDateKeyForGrouping;

    const groupedActionCodeCounts = filteredTransactions.reduce(
        (acc, transaction) => {
            const key = groupKeyFn(transaction.createdAt);
            const actionCode = transaction.actionCode;
            if (!acc[key]) {
                acc[key] = {};
            }
            acc[key][actionCode] = (acc[key][actionCode] || 0) + 1;
            return acc;
        },
        {} as Record<string, Record<string, number>>,
    );

    // Generate complete date range
    const generateDateRange = () => {
        const dates: string[] = [];
        let currentDate = new Date(from);
        const endDate = new Date(to);

        while (currentDate <= endDate) {
            let key: string;

            if (granularity === "month") {
                key = getMonthKeyForGrouping(currentDate);
                // Move to next month
                currentDate = addMonths(currentDate, 1);
            } else if (granularity === "week") {
                key = getWeekStartKey(currentDate);
                // Move to next week
                currentDate = addWeeks(currentDate, 1);
            } else {
                key = getDateKeyForGrouping(currentDate);
                // Move to next day
                currentDate = addDays(currentDate, 1);
            }

            if (!dates.includes(key)) {
                dates.push(key);
            }
        }

        return dates;
    };

    const completeDateRange = generateDateRange();

    return completeDateRange
        .map(key => {
            const dataPoint: Record<string, number | string> = {
                key: String(key),
            };
            actionCodes.forEach(actionCode => {
                dataPoint[actionCode] =
                    groupedActionCodeCounts[key]?.[actionCode] || 0;
            });
            return dataPoint;
        })
        .sort((a, b) => String(a.key).localeCompare(String(b.key)));
};

const RadialChartComponent = ({
    analytics,
}: {
    analytics: CreditTransactionAnalytics;
}) => {
    const totalEarned = analytics.earn.amount + analytics.signUp.amount;
    const totalSpent = Math.abs(analytics.spend.amount);
    const spentPercentage = totalEarned ? (totalSpent / totalEarned) * 100 : 0;

    // Convert percentage to radians for SVG arc
    const spentRadians = (spentPercentage / 100) * 2 * Math.PI;
    const radius = 70;
    const center = 80;

    // Calculate arc endpoints
    const endX = center + radius * Math.cos(spentRadians - Math.PI / 2);
    const endY = center + radius * Math.sin(spentRadians - Math.PI / 2);

    // Determine if we need a large arc flag
    const largeArcFlag = spentRadians > Math.PI ? 1 : 0;

    return (
        <div className="rounded-lg bg-admin-muted p-4">
            <div className="mb-4 flex items-center justify-between">
                <div className="flex flex-col">
                    <h4 className="text-sm font-semibold">Earn vs Spend</h4>
                    <p className="text-sm text-admin-muted-foreground">
                        Total credits earned vs spent
                    </p>
                </div>
            </div>

            <div className="flex items-center justify-center">
                <div className="relative h-40 w-40">
                    <svg className="h-full w-full -rotate-90 transform">
                        {/* Background circle (Earned) */}
                        <circle
                            cx={center}
                            cy={center}
                            r={radius}
                            fill="none"
                            stroke="#4ade80"
                            strokeWidth="20"
                        />

                        {/* Spent arc */}
                        {totalSpent > 0 && (
                            <path
                                d={`M ${center} ${center - radius}
                                    A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY}`}
                                fill="none"
                                stroke="#ef4444"
                                strokeWidth="20"
                            />
                        )}
                    </svg>

                    {/* Center text */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                        <div className="text-2xl font-bold text-admin-foreground">
                            {spentPercentage.toFixed(1)}%
                        </div>
                        <div className="text-xs text-admin-muted-foreground">
                            Spent
                        </div>
                    </div>
                </div>
            </div>

            {/* Legend */}
            <div className="mt-4 flex justify-center gap-6 text-xs">
                <div className="flex items-center gap-2">
                    <div className="h-3 w-3 rounded-full bg-green-400"></div>
                    <span className="text-admin-muted-foreground">
                        Earned: {totalEarned.toLocaleString()}
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="h-3 w-3 rounded-full bg-red-400"></div>
                    <span className="text-admin-muted-foreground">
                        Spent: {totalSpent.toLocaleString()}
                    </span>
                </div>
            </div>
        </div>
    );
};

const ChartComponent = ({
    data,
    actionCodes,
    chartConfig,
    title,
    description,
}: {
    data: any[];
    actionCodes: string[];
    chartConfig: any;
    title: string;
    description: string;
}) => {
    const granularity = getGroupingGranularity(new Date(), new Date());

    const totalTransactions = data.reduce(
        (sum, item) =>
            sum +
            Object.values(item)
                .filter(val => typeof val === "number")
                .reduce((a, b) => a + b, 0),
        0,
    );

    return (
        <div className="rounded-lg bg-admin-muted p-4">
            <div className="mb-2 flex items-center justify-between">
                <div className="flex flex-col">
                    <h4 className="text-sm font-semibold">{title}</h4>
                    <p className="text-sm text-admin-muted-foreground">
                        {description}
                    </p>
                </div>
                <div className="mt-4 flex items-center gap-4 pr-4 text-xs text-admin-muted-foreground">
                    <span className="font-semibold">Total:</span>
                    {totalTransactions}
                </div>
            </div>

            {data.length > 0 ? (
                <ChartContainer
                    config={chartConfig}
                    className="h-64 w-full"
                >
                    <AreaChart
                        data={data}
                        {...{ overflow: "visible" }}
                    >
                        <defs>
                            {actionCodes.map((actionCode, index) => (
                                <linearGradient
                                    key={actionCode}
                                    id={`fill${actionCode}`}
                                    x1="0"
                                    y1="0"
                                    x2="0"
                                    y2="1"
                                >
                                    <stop
                                        offset="5%"
                                        stopColor={`hsl(var(--admin-chart-${index + 1}))`}
                                        stopOpacity={0.8}
                                    />
                                    <stop
                                        offset="95%"
                                        stopColor={`hsl(var(--admin-chart-${index + 1}))`}
                                        stopOpacity={0.1}
                                    />
                                </linearGradient>
                            ))}
                        </defs>
                        <CartesianGrid vertical={false} />
                        <XAxis
                            dataKey="key"
                            tickLine={false}
                            axisLine={false}
                            tick={{ fontSize: 10 }}
                            tickMargin={8}
                            minTickGap={32}
                            tickFormatter={value => {
                                if (granularity === "month") {
                                    // value: yyyy-MM
                                    const [year, month] =
                                        String(value).split("-");
                                    return `${new Date(
                                        Number(year),
                                        Number(month) - 1,
                                    ).toLocaleString("en-US", {
                                        month: "short",
                                        year: "2-digit",
                                    })}`;
                                } else if (granularity === "week") {
                                    // value: yyyy-MM-dd (start of week)
                                    return formatWeekRange(String(value));
                                } else {
                                    // day granularity
                                    return formatDateForDisplay(value, "MMM d");
                                }
                            }}
                        />
                        <YAxis tick={{ fontSize: 12 }} />
                        <ChartTooltip
                            cursor={false}
                            content={
                                <ChartTooltipContent
                                    labelFormatter={value => {
                                        return new Date(
                                            value,
                                        ).toLocaleDateString("en-US", {
                                            month: "short",
                                            day: "numeric",
                                        });
                                    }}
                                    indicator="dot"
                                />
                            }
                        />
                        {actionCodes.map((actionCode, index) => (
                            <Area
                                key={actionCode}
                                dataKey={actionCode}
                                type="natural"
                                fill={`url(#fill${actionCode})`}
                                stroke={`hsl(var(--admin-chart-${index + 1}))`}
                                stackId="a"
                            />
                        ))}
                        <ChartLegend content={<ChartLegendContent />} />
                    </AreaChart>
                </ChartContainer>
            ) : (
                <div className="flex h-64 items-center justify-center text-admin-muted-foreground">
                    No {title.toLowerCase()} data available for the selected
                    period
                </div>
            )}
        </div>
    );
};

export const CreditsAnalytics = ({
    data,
    dateRange,
}: CreditsAnalyticsProps) => {
    const from = useMemo(
        () => dateRange?.from ?? new Date(),
        [dateRange?.from],
    );
    const to = useMemo(() => dateRange?.to ?? new Date(), [dateRange?.to]);
    const granularity = getGroupingGranularity(from, to);

    const spendData = useMemo(
        () =>
            generateChartData(
                data?.transactions || [],
                spendActionCodes,
                granularity,
                from,
                to,
            ),
        [data?.transactions, granularity, from, to],
    );

    const purchaseData = useMemo(
        () =>
            generateChartData(
                data?.transactions || [],
                purchaseActionCodes,
                granularity,
                from,
                to,
            ),
        [data?.transactions, granularity, from, to],
    );

    return (
        <Card className="h-full">
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5" />
                    Credits Analytics
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
                {/* Area Charts */}
                <ChartComponent
                    data={spendData}
                    actionCodes={spendActionCodes}
                    chartConfig={spendChartConfig}
                    title="Credit Spending Trends"
                    description="Credit spending by action type"
                />

                <ChartComponent
                    data={purchaseData}
                    actionCodes={purchaseActionCodes}
                    chartConfig={purchaseChartConfig}
                    title="Credit Purchase Trends"
                    description="Credit purchases by pack type"
                />
                {/* Radial Chart */}
                <RadialChartComponent analytics={data.analytics} />
            </CardContent>
        </Card>
    );
};
